import { useQuery } from "@apollo/client"
import cn from "classnames"
import React, { useCallback, useState, useEffect, useRef, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import { type HelpcenterQuery } from "@/api/graphql"
import ContactUs from "@/common/components/contact_us"
import DotSpinner from "@/common/components/dot_spinner"
import useMedia from "@/common/hooks/use_media"
import tailwindTheme from "@/common/styles/theme.module.css"
import HelpArticles from "@/help_center/components/help_articles"
import HelpQuestions from "@/help_center/components/help_questions"
import HelpSearch from "@/help_center/components/help_search"
import {
  FaqOutline,
  Search,
  CloseLarge,
  ArrowLeft,
  TruckOutline,
  BoxOutline,
  EditOutline,
  ProfileOutline,
  EnvelopeOutline,
} from "@/skylticons"
import "@/help_center/styles/index.css"

const helpCenterDoc = graphql(`
  query Helpcenter {
    helpCategories {
      id
      name
      tag

      articles {
        id
        title
        content
      }

      questions {
        id
        question
        answer
      }
    }

    helpQuestions(frontPage: true) {
      id
      question
      answer
    }
  }
`)

type HelpCategory = HelpcenterQuery["helpCategories"][0]
type HelpArticle = HelpCategory["articles"][0]

interface HelpHeaderProps {
  start?: boolean
  disabled?: boolean
  showToggleSearch?: boolean
  allArticles?: HelpArticle[]
  toggleModal: () => void
  goBack: () => void
  updateArticle: (article: HelpArticle) => void
}

const Header: React.FC<HelpHeaderProps> = ({
  start,
  disabled,
  toggleModal,
  goBack,
  showToggleSearch,
  updateArticle,
  allArticles,
}) => {
  const { t } = useTranslation()
  const [showSearch, setShowSearch] = useState(true)

  const toggleSearch = useCallback(() => {
    setShowSearch(v => !v)
  }, [])

  useEffect(() => {
    setShowSearch(!showToggleSearch)
  }, [showToggleSearch])

  return (
    <>
      <div className="header-row">
        {(start || disabled) && <h4 className="mb-0">{t("help_center.header")}</h4>}
        {!start && !disabled && (
          <button className="button button-secondary button-light mb-0" onClick={() => goBack()} type="button">
            <ArrowLeft />
            {t("toolbox.back")}
          </button>
        )}

        <div className="buttons-right">
          {!disabled && showToggleSearch && (
            <button
              className={cn("button button-primary mb-0", { search_visible: showSearch })}
              onClick={toggleSearch}
              type="button"
            >
              <Search />
            </button>
          )}
          <button className="button button-secondary button-light mb-0" onClick={toggleModal} type="button">
            <CloseLarge />
            {t("misc.close")}
          </button>
        </div>
      </div>

      {!disabled && showSearch && allArticles && (
        <HelpSearch idPrefix="widget" updateArticle={updateArticle} allArticles={allArticles} />
      )}
    </>
  )
}

const renderCategories = (
  categories: HelpCategory[],
  updateCategory: (category: HelpCategory) => void,
  header: string
) => {
  return (
    <div className="section margin">
      <p className="categories-section-header bold">{header}</p>
      <div className="categories">
        {categories.map(category => {
          return (
            <div key={category.name} className="card" onClick={() => updateCategory(category)}>
              {categoryIcon(category.tag)}
              <p className="paragraph-xs mb-0">{category.name}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const categoryIcon = (tag: string) => {
  switch (tag) {
    case "product_information":
      return <BoxOutline />
    case "shipping_and_payment":
      return <TruckOutline />
    case "how_to_order":
      return <EditOutline />
    case "my_account":
      return <ProfileOutline />
    default:
      return
  }
}

const renderArticle = (article: HelpArticle) => {
  return (
    <div className="card article">
      <h3 className="mb-0">{article.title}</h3>
      <hr />
      <p dangerouslySetInnerHTML={{ __html: article.content || "" }}></p>
    </div>
  )
}

interface HelpContactProps {
  updateTab: (tab: string) => void
}

const HelpContact: React.FC<HelpContactProps> = ({ updateTab }) => {
  const { t } = useTranslation()

  return (
    <div className="section contact">
      <p className="section-header bold">{t("pages.cant_find")}</p>
      <button
        className="button button-secondary full-width mb-0"
        type="button"
        onClick={() => updateTab("contact")}
        data-gtm="contact-us"
      >
        <EnvelopeOutline />
        {t("menu.contact")}
      </button>
    </div>
  )
}

interface HelpModalProps {
  toggleModal: () => void
}

const HelpModal: React.FC<HelpModalProps> = ({ toggleModal }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery(helpCenterDoc)
  const [tab, setTab] = useState<"start" | "category" | "article" | "contact">("start")
  const [category, setCategory] = useState<HelpCategory>()
  const [article, setArticle] = useState<HelpArticle>()
  const categories = data?.helpCategories

  const goBack = useCallback(() => {
    if (article && category) {
      setTab("category")
    } else {
      setTab("start")
      setCategory(undefined)
    }

    scrollUp()
    setArticle(undefined)
  }, [article, category, setTab, setCategory])

  const updateCategory = useCallback(
    (category: HelpCategory) => {
      setTab("category")
      setCategory(category)
      scrollUp()
    },
    [setTab]
  )

  const updateArticle = useCallback(
    (article: HelpArticle) => {
      setTab("article")
      setArticle(article)
      if (!category && categories) {
        setCategory(categories.filter(category => category.articles.includes(article))[0])
      }
      scrollUp()
    },
    [setTab, categories, category]
  )

  const updateTab = useCallback(
    (tab: "start" | "category" | "article" | "contact") => {
      setTab(tab)
      scrollUp()
    },
    [setTab]
  )

  const scrollUp = () => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }

  const contentRef = useRef<HTMLDivElement>(null)
  const allArticles = useMemo(() => {
    return categories?.map(category => category.articles).flat() || []
  }, [categories])

  if (!data || loading) {
    return (
      <div id="help-center" className="z-30">
        <div className="widget-header z-10">
          <Header disabled toggleModal={toggleModal} goBack={goBack} updateArticle={updateArticle} />
        </div>

        <div className="content flex items-center justify-center">
          <DotSpinner />
        </div>
      </div>
    )
  }

  const otherCategories = categories?.filter(c => c.name != category?.name) || []
  const articles = category?.articles || []
  const questions = category?.questions || []
  const related_articles = category?.articles.filter(a => a.title != article?.title)

  const frontPageQuestions = data.helpQuestions || []

  return (
    <div id="help-center" className="z-30">
      <div className="widget-header z-10">
        <Header
          start={tab == "start"}
          toggleModal={toggleModal}
          goBack={goBack}
          showToggleSearch={tab == "article"}
          updateArticle={updateArticle}
          allArticles={allArticles}
        />
      </div>

      <div className="content" ref={contentRef}>
        {tab == "start" && (
          <>
            {categories && renderCategories(categories, updateCategory, t("help_center.categories"))}
            <HelpQuestions questions={frontPageQuestions} klass="card_2" />
            <hr />
            <HelpContact updateTab={updateTab} />
          </>
        )}

        {tab == "category" && category != undefined && (
          <>
            <div className="category-header">
              {categoryIcon(category.tag)}
              <h4 className="mb-0">{category.name}</h4>
            </div>
            <HelpQuestions questions={questions} klass="card_1" />
            {articles.length > 0 && (
              <HelpArticles
                articles={articles}
                header={t("help_center.articles")}
                updateArticle={updateArticle}
                klass="card_2"
              />
            )}
            <hr />
            {renderCategories(otherCategories, updateCategory, t("help_center.other_categories"))}
            <hr />
            <HelpContact updateTab={updateTab} />
          </>
        )}

        {tab == "article" && article != undefined && (
          <>
            {renderArticle(article)}
            {related_articles && related_articles.length > 0 && (
              <HelpArticles
                articles={related_articles}
                header={t("help_center.related_articles")}
                updateArticle={updateArticle}
              />
            )}
          </>
        )}

        {tab == "contact" && (
          <>
            <div className="category-header">
              <EnvelopeOutline />
              <h4 className="mb-0">{t("help_center.contact_us")}</h4>
            </div>
            <ContactUs scrollUp={scrollUp} />
          </>
        )}
      </div>
    </div>
  )
}

export interface HelpCenterProps {
  signomatic: boolean
}

const HelpCenter: React.FC<HelpCenterProps> = ({ signomatic }) => {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)

  const toggleModal = useCallback(() => {
    setModalOpen(v => !v)
  }, [])

  const overlayClick = useCallback((e: MouseEvent) => {
    if (e.target instanceof Element) {
      if (!e.target.closest("#help-center")) {
        setModalOpen(false)
      }
    }
  }, [])

  useEffect(() => {
    document.addEventListener("click", overlayClick, { capture: true })

    return () => document.removeEventListener("click", overlayClick, { capture: true })
  }, [overlayClick])

  const mobile = useMedia(`(max-width: ${tailwindTheme.screensMd})`)
  const iconOnly = signomatic && mobile

  return (
    <>
      <div className="help-widget-open z-20 flex flex-col">
        <button
          className={cn("button button-primary button-medium mb-0 ml-0 mt-3", { "icon-only": iconOnly })}
          type="button"
          onClick={toggleModal}
          data-gtm="widget-open"
        >
          <FaqOutline />
          {!iconOnly && <span>{t("signomatic.help")}</span>}
        </button>
      </div>

      {modalOpen && <HelpModal toggleModal={toggleModal} />}
    </>
  )
}

export default HelpCenter
